import styled from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
`;

/* 좌측 액센트 바로 '새 느낌' */
export const Shell = styled.section<{
  $accent?: "indigo" | "emerald" | "sky" | "zinc";
}>`
  --indigo: #6366f1;
  --emerald: #10b981;
  --sky: #0ea5e9;
  --zinc: #71717a;
  --accent: ${({ $accent }) =>
    $accent === "emerald"
      ? "var(--emerald)"
      : $accent === "sky"
      ? "var(--sky)"
      : $accent === "zinc"
      ? "var(--zinc)"
      : "var(--indigo)"};

  position: relative;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  overflow: hidden;

  padding: 16px;
  display: grid;
  gap: 14px;

  /* 좌측 액센트 바 */
  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 4px;
    background: linear-gradient(
      180deg,
      color-mix(in srgb, var(--accent), #ffffff 30%) 0%,
      var(--accent) 14%,
      var(--accent) 86%,
      color-mix(in srgb, var(--accent), #ffffff 30%) 100%
    );
  }
`;

export const Header = styled.div`
  display: grid;
  gap: 6px;
`;
export const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
`;
export const Desc = styled.p`
  margin: 0;
  font-size: 13px;
  color: #475569;
`;

export const GroupTitle = styled.h3`
  margin: 8px 0 0 0;
  font-size: 14px;
  font-weight: 900;
  color: #334155;
  letter-spacing: 0.2px;
`;

export const FormGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: grid;
  gap: 6px;
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 800;
  color: #64748b;
`;

export const Hint = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

const baseInput = `
  height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;

  &:focus {
    border-color: #cbd5e1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15); /* indigo ring */
  }
`;

export const Input = styled.input`
  ${baseInput}
`;

export const NumberInput = styled.input.attrs({ type: "number" })`
  ${baseInput}
`;

export const Select = styled.select`
  ${baseInput}
  appearance: none;
`;

export const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
export const Pill = styled.button<{ $active?: boolean }>`
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? "#6366f1" : "#e5e7eb")};
  background: ${({ $active }) => ($active ? "#eef2ff" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#3730a3" : "#334155")};
  font-weight: 800;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease,
    transform 0.1s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "#e0e7ff" : "#f8fafc")};
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

export const Toggle = styled.button<{ $on?: boolean }>`
  width: 56px;
  height: 30px;
  border-radius: 999px;
  position: relative;
  cursor: pointer;
  border: 1px solid ${({ $on }) => ($on ? "#16a34a" : "#cbd5e1")};
  background: ${({ $on }) => ($on ? "#22c55e" : "#f1f5f9")};
  transition: background 0.15s ease, border-color 0.15s ease;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.25);
  }

  &:hover {
    border-color: ${({ $on }) => ($on ? "#16a34a" : "#94a3b8")};
  }
`;

export const ToggleKnob = styled.span<{ $on?: boolean }>`
  position: absolute;
  top: 50%;
  left: ${({ $on }) => ($on ? "calc(100% - 26px)" : "4px")};
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.12);
  transition: left 0.15s ease;
`;

export const RowEnd = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

export const PrimaryButton = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #22c55e;
  background: #22c55e;
  color: #ffffff;
  font-weight: 900;
  cursor: pointer;
  transition: filter 0.12s ease, transform 0.1s ease;

  &:hover {
    filter: brightness(0.96);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const GhostButton = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #0f172a;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease,
    transform 0.1s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

export const ErrorBox = styled.div`
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 12px;
  padding: 12px;
`;

/* 로딩 스켈레톤 */
export const SkeletonGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(240px, 1fr));

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }

  .skeleton {
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
    background-size: 400% 100%;
    animation: shimmer 1.4s ease infinite;
    border: 1px solid #e5e7eb;
  }

  @keyframes shimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }
`;
